import React, { useState, useEffect, useMemo } from "react";
import Cookies from "js-cookie";
import Fuse from "fuse.js";
import KeywordSearchResults from "./KeywordSearchResults";
import ResourceMap from "./ResourceMap";
import LocationFilter from "./LocationFilter";
import FilterSort from "./FilterSort";
import Tabs from "./Tabs";

const FUSE_OPTIONS = {
  shouldSort: true,
  threshold: 0.15,
  // location: 0, // unused from ignoreLocation: true
  // distance: 1000, // unused from ignoreLocation: true
  minMatchCharLength: 3,
  ignoreLocation: true,
  // includeMatches: true,
  includeScore: true,
  keys: ["name_primary", "description"], // Todd mentioned that description should have the highest weight. Maybe don't even use search hints?
};

export default function Results() {
  const isDev = window.location.hostname.includes("localhost");
  const [responseData, setResponseData] = useState([]); // window.searchResults
  const [results, setResults] = useState([]);
  const [keywordFilter, setKeywordFilter] = useState(""); // this is the secondary filter
  const [distanceFilter, setDistanceFilter] = useState(
    Cookies.get("distanceFilter") ? JSON.parse(Cookies.get("distanceFilter")) : null
  );

  const [isKeywordSearch, setIsKeywordSearch] = useState(window.isKeywordSearch);
  const [keyword, setKeyword] = useState(window.keyword);
  const [topics, setTopics] = useState([]); // window.subtopics
  const [userList, setUserList] = useState([]);
  const [isMobile, setIsMobile] = useState(!window.matchMedia("(min-width: 768px)").matches);
  const [newSearchKeyword, setNewSearchKeyword] = useState(keyword);
  let defaultSort = "relevance";
  if (distanceFilter && distanceFilter.lat && distanceFilter.lng) {
    defaultSort = "distance";
  } else if (!isKeywordSearch) {
    defaultSort = "alphabetical";
  }
  const [sortingOption, setSortingOption] = useState(defaultSort); // auto, alphabetical, relevance, keywordfilter, distance
  const isAgencySearch = window.location.search.includes("type=agency");
  const sortResults = (results, method) => {
    let sortedResults = [];

    if (method === "alphabetical") {
      sortedResults = results.sort((a, b) => (a.item.name_primary > b.item.name_primary ? 1 : -1));
    } else if (method === "relevance") {
      sortedResults = results.sort((a, b) => (a.item.searchScore < b.item.searchScore ? 1 : -1)); // this is searchScore, not fuse score....
    } else if (method === "distance") {
      sortedResults = results.sort((a, b) => {
        return a.distance - b.distance;
      });
    } else {
      sortedResults = results;
    }

    return sortedResults;
  };

  // watch for screen size shange, and update isMobile state
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    // listen for window resize
    const handleResize = () => {
      setIsMobile(!mediaQuery.matches);
    };
    mediaQuery.addEventListener("change", handleResize);

    return () => {
      mediaQuery.removeEventListener("change", handleResize);
    };
  }, []);

  useEffect(() => {
    const list = Cookies.get("userList");
    if (list) {
      const parsedList = JSON.parse(list);
      setUserList(parsedList);
    }
  }, []);

  useEffect(() => {
    if (distanceFilter && distanceFilter.lat && distanceFilter.lng) {
      Cookies.set("userLocation", [distanceFilter.lat, distanceFilter.lng].join(","));
    } else {
      setSortingOption(isKeywordSearch ? "relevance" : "alphabetical");
      Cookies.set("userLocation", "");
    }
    Cookies.set("distanceFilter", JSON.stringify(distanceFilter));
  }, [distanceFilter]);

  useEffect(() => {
    if (
      sortingOption === "distance" &&
      (!distanceFilter || !distanceFilter.lat || !distanceFilter.lng)
    ) {
      setSortingOption(isKeywordSearch ? "relevance" : "alphabetical");
    }
  }, [sortingOption]);

  const handleUserListUpdate = (id) => {
    const updatedList = userList.includes(id)
      ? userList.filter((item) => item !== id)
      : [...userList, id];
    setUserList(updatedList);
    Cookies.set("userList", JSON.stringify(updatedList));

    if (!isDev) {
      gtag("event", userList.includes(id) ? "remove_from_list" : "add_to_list", {});
    }
  };

  useEffect(() => {
    const yourListButton = document.querySelector(".header-tools-list");
    if (yourListButton) {
      if (userList.length === 0) {
        yourListButton.innerHTML = "<span>Your</span> List";
      } else {
        yourListButton.innerHTML = `<span>Your</span> List <span>(${userList.length})</span>`;
      }
    }
  }, [userList]);

  const tabData = [
    {
      title: "List View",
      id: "list",
      content: (
        <KeywordSearchResults
          results={results}
          userList={userList}
          handleUserListUpdate={handleUserListUpdate}
          topics={topics}
          setTopics={setTopics}
          isKeywordSearch={isKeywordSearch}
          isMobile={isMobile}
          keyword={keyword}
          numResults={responseData.length}
        />
      ),
    },
    {
      title: "Map View",
      id: "map",
      content: (
        <>
          <ResourceMap
            results={results}
            userList={userList}
            isMobile={isMobile}
            handleUserListUpdate={handleUserListUpdate}
          />
        </>
      ),
    },
  ];

  // get data from window.searchResults on load
  useEffect(() => {
    // In both cases trigger the related effects
    setResponseData(window.searchResults);
    setTopics(window.topics ? window.topics : []);
  }, []);

  const fuse = useMemo(() => {
    return responseData ? new Fuse(responseData, FUSE_OPTIONS) : null;
  }, [responseData]);

  // Update results when filters change
  useEffect(() => {
    // filter by keyword
    let nextResults =
      keywordFilter.length > 0 && fuse
        ? fuse.search(keywordFilter)
        : responseData?.map((resource) => ({ item: resource }));

    // add distance to results
    if (Cookies.get("userLocation")) {
      nextResults = nextResults.map((result) => {
        const distance = result.item.lat_lng
          ? getDistance(Cookies.get("userLocation"), result.item.lat_lng)
          : null;
        return { ...result, distance };
      });
    }

    nextResults = filterResultsByTopic(nextResults);

    nextResults = sortResults(nextResults, sortingOption);

    // filter by distance from "Within" filter
    nextResults = filterResultsByDistance(nextResults);

    setResults(nextResults);
  }, [keywordFilter, distanceFilter, fuse, topics, sortingOption]);

  function filterResultsByTopic(initialResults) {
    if (topics.length === 0 && isKeywordSearch && !isAgencySearch) {
      // init topics for keyword search
      // loop through initial results, for each new found in result.item.taxonomy, add to new array
      let parentTopics = [];
      initialResults.map((result) => {
        result.item.taxonomy.forEach((topic) => {
          if (!parentTopics.some((activeTopic) => activeTopic.id === topic.id)) {
            // add active = true to each topic
            topic.active = true;
            parentTopics.push(topic);
          }
        });
      });

      parentTopics.sort((a, b) => a.name.localeCompare(b.name));
      setTopics(parentTopics);
    }
    const activeTopics = topics.filter((topic) => topic.active);
    const activeTopicIds = activeTopics.map((topic) => topic.id);
    const filteredResults = initialResults.filter((result) => {
      // include "other" in All results, if no topics let everything through
      const allSelected = activeTopics.length == topics.length
      if ((result.item.taxonomy.length === 0 && allSelected) || topics.length === 0) {
        return true;
      }

      // if result has any of the active topics in it's taxonomy, return true
      if (activeTopicIds.some((topic) => result.item.taxonomy.map((t) => t.id).includes(topic))) {
        return true;
      }
      // if result has any of the active topics in it's childTopics, return true
      const childTopics = result.item.taxonomy
        .map((topic) => topic.children.map((child) => child.id))
        .flat();
      if (activeTopicIds.some((topic) => childTopics.includes(topic))) {
        return true;
      }
    });
    return filteredResults;
  }

  function filterResultsByDistance(initialResults) {
    if (!distanceFilter || !distanceFilter.lat || !distanceFilter.lng) return initialResults;
    return initialResults.filter((result) => {
      return (
        result.item.bcWide ||
        (result.distance && Number(result.distance) <= Number(distanceFilter.distance))
      );
    });
  }

  function getDistance(coord1, coord2) {
    coord1 = coord1.split(",");
    coord2 = coord2.split(",");
    const lat1 = parseFloat(coord1[0]);
    const lon1 = parseFloat(coord1[1]);
    const lat2 = parseFloat(coord2[0]);
    const lon2 = parseFloat(coord2[1]);

    const theta = lon1 - lon2;
    let dist =
      Math.sin(deg2rad(lat1)) * Math.sin(deg2rad(lat2)) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.cos(deg2rad(theta));
    dist = Math.acos(dist);
    dist = rad2deg(dist);
    let distance = dist * 60 * 1.1515 * 1.609344;
    distance = distance.toFixed(2);
    return distance;
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  function rad2deg(rad) {
    return rad * (180 / Math.PI);
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    if (!newSearchKeyword) return;
    const agencyParam = isAgencySearch ? "type=agency&" : "";
    window.location.href = `/result/?${agencyParam}keyword=${newSearchKeyword}`;
  }

  return (
    <div className="results">
      <section className="section">
        <div className="content content--wide content--filters">
          {!isKeywordSearch && (
            <a href="/" className="link-back">
              <span className="iconBg-right" />
              Start a new search
            </a>
          )}
          <div className={`overview ${isKeywordSearch ? "overview--keyword" : ""}`}>
            {isKeywordSearch ? (
              <form id="searchForm" onSubmit={handleSearchSubmit}>
                <h1>Search {isAgencySearch ? "agencies" : "resources"}</h1>
                <div className="overview-input">
                  <input
                    className="h1"
                    type="search"
                    name="keyword"
                    id="keyword"
                    aria-label="Start a new Search by Keyword"
                    value={newSearchKeyword}
                    autoComplete="off"
                    onChange={(e) => setNewSearchKeyword(e.target.value)}
                  />
                  <button className="button button--secondary button--l icon-arrow" type="submit">
                    Search
                  </button>
                </div>
              </form>
            ) : (
              <h1>
                {responseData.length !== 1 ? "Resources" : "Resource"} for:{" "}
                {topics.length > 0 && topics[0].parent_topic}
              </h1>
            )}
          </div>
          <LocationFilter
            distanceFilter={distanceFilter}
            setDistanceFilter={setDistanceFilter}
            isMobile={isMobile}
          />
        </div>
      </section>
      <FilterSort
        keywordFilter={keywordFilter}
        setKeywordFilter={setKeywordFilter}
        sortingOption={sortingOption}
        setSortingOption={setSortingOption}
      />
      <Tabs tabs={tabData} />
    </div>
  );
}
